import type { Plan } from "@/Modules/Models/Domain/Plan";
import type { PlanApi } from "@/Modules/Models/Api/Plan";
import type { TransformerExtended } from "@/Common/Models/Domain/Transformer";
import type { PlanUi } from "../Plan/Models/Ui/PlanUi";
import type { CurrencyUi } from "@/Common/Models/Ui/CurrencyUi";
import { currencyToCurrencyUiMap } from "@/Common/Models/Ui/currencyToCurrencyUiMap";

import DefaultTransformerGenerator from "@/Common/Transformers/FunctionalBase";

import { getNowInSeconds } from "@/Utils/Date";
import { hasValue } from "@/Utils/Objects";

const PlanTransformer: TransformerExtended<Plan, PlanApi, PlanUi> = {
  ...DefaultTransformerGenerator<Plan, PlanApi, PlanUi>(),

  toDomain(planApi: PlanApi): Plan {
    const discountPercentage =
      planApi.discount_percentage > 0 ? planApi.discount_percentage : planApi.savings_percentage;
    const discount = Math.round(discountPercentage * 100);
    const isDiscountOnlyForFirstSubscription = planApi.is_first_time && planApi.discount_percentage > 0;
    const isPaymentMethodRequired = planApi.is_payment_method_required;
    let initialSessionPrice: number | null = null;

    if (planApi.customer_price_per_session !== planApi.price_per_session) {
      initialSessionPrice = planApi.price_per_session;
    }

    const plan: Plan = {
      currency: planApi.currency,
      description: planApi.description,
      discount: discount,
      id: planApi.uuid,
      initialSessionPrice: initialSessionPrice,
      isActive: () => planApi.is_active,
      isDiscountOnlyForFirstSubscription: isDiscountOnlyForFirstSubscription,
      isPrivate: planApi.is_maintenance,
      isPromo: planApi.is_promo,
      price: planApi.price,
      promoPlanUuid: planApi.promo_plan_uuid,
      promoPlanDescription: planApi.promo_plan_description,
      sessionPrice: planApi.customer_price_per_session,
      title: planApi.title,
      customerPrice: planApi.customer_price,
      isPaymentMethodRequired: isPaymentMethodRequired,
      startAt: planApi.start_at,
      endAt: planApi.end_at,
      videoCalls: planApi.video_calls,
      sessionDuration: planApi.session_duration,
    };

    plan.isActive = () => plan.startAt <= getNowInSeconds() && plan.endAt > getNowInSeconds();

    return plan;
  },

  toUi(plan: Plan): PlanUi {
    const currencyUi: CurrencyUi = currencyToCurrencyUiMap[plan.currency];
    const initialSessionPrice = plan.initialSessionPrice ? `${plan.initialSessionPrice}${currencyUi}` : null;
    const planUi: PlanUi = {
      description: plan.description,
      discount: plan.discount,
      id: plan.id,
      initialSessionPrice: initialSessionPrice,
      isDiscountOnlyForFirstSubscription: plan.isDiscountOnlyForFirstSubscription,
      isPromo: plan.isPromo,
      promoPlanDescription: plan.promoPlanDescription,
      isPrivate: plan.isPrivate,
      isActive: () => plan.startAt <= getNowInSeconds() && plan.endAt > getNowInSeconds(),
      sessionPrice: `${plan.sessionPrice}${currencyUi}`,
      title: plan.title,
      subscribeText: getButtonSubscribeText(plan),
      customerPrice: `${plan.customerPrice}${currencyUi}`,
      isPaymentMethodRequired: plan.isPaymentMethodRequired,
      sessionDuration: plan.sessionDuration,
    };

    return planUi;
  },
};

const getButtonSubscribeText = (plan: Plan): string => {
  return hasValue(plan.promoPlanUuid) && hasValue(plan.promoPlanDescription)
    ? "settings.subscription.plan.plan_card_try_it_now"
    : "settings.subscription.plan.plan_card_subscribe_me";
};

export default PlanTransformer;
